import {
  createCustomConfig,
  getCustomConfigByKey,
  updateCustomConfigById,
} from "../Reports/Components/utils";
import {
  emptyQuickFilterValue,
  getPageQuickFilters,
  getQuickFilterKey,
  normalizeQuickFilter,
  MAX_USER_QUICK_FILTERS_PER_PAGE,
  QUICK_FILTER_SOURCE,
  QUICK_FILTER_VERSION,
} from "./quickFilterUtils";

const getConfigId = (config) => config?.id ?? config?._id ?? null;

export const getQuickFilterConfig = async (clientId, platform) => {
  const key = getQuickFilterKey(platform);
  const config = await getCustomConfigByKey(clientId, key);
  const value = config?.value ?? emptyQuickFilterValue(platform);

  return {
    key,
    configId: getConfigId(config),
    value: {
      platform: value?.platform ?? platform,
      version: value?.version ?? QUICK_FILTER_VERSION,
      pages: value?.pages && typeof value.pages === "object" ? value.pages : {},
    },
  };
};

export const savePageQuickFilters = async ({
  clientId,
  platform,
  pageId,
  pageConfig,
  existingConfig = null,
}) => {
  const key = getQuickFilterKey(platform);
  const current =
    existingConfig ?? (await getQuickFilterConfig(clientId, platform));

  const nextValue = {
    platform,
    version: QUICK_FILTER_VERSION,
    pages: {
      ...(current?.value?.pages || {}),
      [pageId]: {
        filters: Array.isArray(pageConfig?.filters) ? pageConfig.filters : [],
      },
    },
  };

  const payload = { key, value: nextValue };

  if (current?.configId) {
    await updateCustomConfigById(clientId, current.configId, payload);
    return {
      key,
      configId: current.configId,
      value: nextValue,
    };
  }

  await createCustomConfig(clientId, payload);
  const refreshed = await getQuickFilterConfig(clientId, platform);
  return {
    key,
    configId: refreshed?.configId ?? null,
    value: refreshed?.value ?? nextValue,
  };
};

export const upsertUserQuickFilterOnPage = async ({
  clientId,
  platform,
  pageId,
  filter,
  existingConfig = null,
}) => {
  const current =
    existingConfig ?? (await getQuickFilterConfig(clientId, platform));
  const pageConfig = getPageQuickFilters(current.value, pageId);
  const normalized = normalizeQuickFilter({
    ...filter,
    source: QUICK_FILTER_SOURCE.USER,
    updatedAt: new Date().toISOString(),
  });

  if (!normalized) {
    throw new Error("Quick filter name is required");
  }

  const filters = [...pageConfig.filters];
  const index = filters.findIndex((item) => item.id === normalized.id);
  if (index >= 0) {
    filters[index] = {
      ...filters[index],
      ...normalized,
      source: QUICK_FILTER_SOURCE.USER,
    };
  } else {
    if (filters.length >= MAX_USER_QUICK_FILTERS_PER_PAGE) {
      throw new Error(
        `You can create up to ${MAX_USER_QUICK_FILTERS_PER_PAGE} quick filters per page.`
      );
    }
    filters.push(normalized);
  }

  return savePageQuickFilters({
    clientId,
    platform,
    pageId,
    pageConfig: { filters },
    existingConfig: current,
  });
};

export const renameUserQuickFilterOnPage = async ({
  clientId,
  platform,
  pageId,
  filterId,
  name,
  existingConfig = null,
}) => {
  const trimmed = `${name || ""}`.trim();
  if (!trimmed) {
    throw new Error("Quick filter name is required");
  }

  const current =
    existingConfig ?? (await getQuickFilterConfig(clientId, platform));
  const pageConfig = getPageQuickFilters(current.value, pageId);
  const filters = pageConfig.filters.map((filter) =>
    filter.id === filterId
      ? {
          ...filter,
          name: trimmed,
          updatedAt: new Date().toISOString(),
        }
      : filter
  );

  return savePageQuickFilters({
    clientId,
    platform,
    pageId,
    pageConfig: { filters },
    existingConfig: current,
  });
};

export const deleteUserQuickFilterOnPage = async ({
  clientId,
  platform,
  pageId,
  filterId,
  existingConfig = null,
}) => {
  const current =
    existingConfig ?? (await getQuickFilterConfig(clientId, platform));
  const pageConfig = getPageQuickFilters(current.value, pageId);
  const filters = pageConfig.filters.filter((filter) => filter.id !== filterId);

  return savePageQuickFilters({
    clientId,
    platform,
    pageId,
    pageConfig: { filters },
    existingConfig: current,
  });
};
